import { usePathname, useSearchParams } from '@/lib/compat/navigation';
import Link from '@/lib/compat/link';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { MobileHeader } from '@/components/layout/mobile-header';
import { PageHeaderDesktop } from '@/components/layout/page-header-desktop';
import { DateRangePicker } from '@/components/reports/date-range-picker';
import type { DateRange } from '@/lib/widgets/types';
import { DATE_RANGE_PRESETS } from '@/lib/widgets/date-ranges';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useRouter } from '@/lib/compat/navigation';
import { Suspense, useCallback } from 'react';

const tabs = [
    { href: '/reports', labelKey: 'reports.overview', exact: true },
    { href: '/reports/sales', labelKey: 'reports.sales' },
    { href: '/reports/orders', labelKey: 'reports.orders' },
    { href: '/reports/products', labelKey: 'reports.products' },
    { href: '/reports/customers', labelKey: 'reports.customers' },
    { href: '/reports/financial', labelKey: 'reports.financial' },
    { href: '/reports/loyalty', labelKey: 'reports.loyalty' },
];

function ReportsLayoutContent({ children }: { children: React.ReactNode }) {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const router = useRouter();

    const dateRangeKey = searchParams.get('range') || 'last30days';
    const customStart = searchParams.get('start');
    const customEnd = searchParams.get('end');
    const customRange: DateRange | undefined =
        dateRangeKey === 'custom' && customStart && customEnd
            ? { start: customStart, end: customEnd }
            : undefined;

    const handleDateRangeChange = useCallback((value: string, range?: DateRange) => {
        const params = new URLSearchParams(searchParams.toString());
        params.set('range', value);
        if (value === 'custom' && range) {
            params.set('start', range.start);
            params.set('end', range.end);
        } else {
            params.delete('start');
            params.delete('end');
        }
        router.replace(`${pathname}?${params.toString()}`);
    }, [pathname, searchParams, router]);

    /** Build tab href preserving the date range param */
    const getTabHref = (href: string) => {
        const params = new URLSearchParams();
        if (dateRangeKey !== 'last30days') {
            params.set('range', dateRangeKey);
        }
        if (customRange) {
            params.set('start', customRange.start);
            params.set('end', customRange.end);
        }
        const qs = params.toString();
        return qs ? `${href}?${qs}` : href;
    };

    return (
        <DashboardLayout>
            <div className="lg:hidden">
                <MobileHeader title={t('reports.title')} />
            </div>

            <div className="hidden lg:block">
                <PageHeaderDesktop variant="list" title={t('reports.title')} />
            </div>

            {/* Tab Navigation + Date Range */}
            <div className="px-4 lg:px-0 pt-2 lg:pt-0">
                <div className="flex items-center justify-between gap-2 border-b">
                    <div className="flex gap-1 overflow-x-auto">
                        {tabs.map((tab) => {
                            const isActive = tab.exact
                                ? pathname === tab.href
                                : pathname.startsWith(tab.href);
                            return (
                                <Link
                                    key={tab.href}
                                    href={getTabHref(tab.href)}
                                    className={cn(
                                        'px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
                                        isActive
                                            ? 'border-primary text-primary'
                                            : 'border-transparent text-muted-foreground hover:text-foreground',
                                    )}
                                >
                                    {t(tab.labelKey)}
                                </Link>
                            );
                        })}
                    </div>
                    <div className="shrink-0 pb-1">
                        <DateRangePicker
                            value={dateRangeKey}
                            customRange={customRange}
                            onChange={handleDateRangeChange}
                        />
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-4 lg:px-0 pt-4 space-y-6 pb-24 lg:pb-6">
                {children}
            </div>
        </DashboardLayout>
    );
}

export default function ReportsLayout({ children }: { children: React.ReactNode }) {
    return (
        <Suspense
            fallback={
                <DashboardLayout>
                    <div className="px-4 lg:px-0 pt-4">
                        <div className="h-9 w-40 rounded-md bg-muted" />
                    </div>
                </DashboardLayout>
            }
        >
            <ReportsLayoutContent>{children}</ReportsLayoutContent>
        </Suspense>
    );
}
